import {
  SDK,
  CredentialFactory,
  type SdkOptions,
  type ICredentialData
} from '@tickethead/sdk';
import localforage from 'localforage';
import account from '../api/account';
import { initAuth } from '~/composables/useAuth';
import { defineNuxtPlugin } from '~/.nuxt/imports';

// If the stored version doesn't match CURRENT_AUTH_STATE_VERSION, every
// persisted auth/session key is wiped and the new version is written back —
// a clean, logged-out boot instead of new code reading old-shaped data. This
// only fires once per browser per version bump, so bump the version again
// whenever a future change breaks persisted-state compatibility the same way.
const CURRENT_AUTH_STATE_VERSION = '1';
const AUTH_STATE_VERSION_STORAGE_KEY = 'authStateVersion';

const PERSISTED_PINIA_STORE_KEYS = [
  'user',
  'app',
  'alert',
  'sendTickets',
  'sendNFT',
  'inviteOrganizer',
  'events',
  'orders',
  'questionnaire',
  'organizerSignup'
];

// @nuxtjs/auth-next's `auth` Vuex module, persisted by plugins/authPersistence.ts — remove this line alongside that file when @nuxtjs/auth-next itself is removed at Stage B.
const LEGACY_VUEX_AUTH_STORAGE_KEY = 'BAMauth';

const sdkKey = 'SDK';

async function enforceAuthStateVersion(localStore: LocalForage): Promise<void> {
  try {
    if (
      localStorage.getItem(AUTH_STATE_VERSION_STORAGE_KEY) ===
      CURRENT_AUTH_STATE_VERSION
    ) {
      return;
    }

    PERSISTED_PINIA_STORE_KEYS.forEach((key) => localStorage.removeItem(key));
    localStorage.removeItem(LEGACY_VUEX_AUTH_STORAGE_KEY);

    await localStore.removeItem(sdkKey);

    localStorage.setItem(
      AUTH_STATE_VERSION_STORAGE_KEY,
      CURRENT_AUTH_STATE_VERSION
    );
  } catch (e) {
    // Storage can throw (Safari private mode, blocked storage, quota errors) — fail open rather than block boot over a check with nothing reliable to act on.
    console.warn('[enforceAuthStateVersion] skipped:', e);
  }
}

export default defineNuxtPlugin(async (nuxtApp) => {
  const { app } = nuxtApp.nuxt2Context;
  const { $auth, $config, provide } = nuxtApp;

  // @nuxtjs/auth-next's own token storage (`auth._token.local`,
  // `auth._refresh_token.local`, and their expiration counterparts) is now
  // unused — nothing reads $auth for identity anymore (see useAuth()), and
  // nothing else writes or clears it. Reset it unconditionally so an
  // existing session's tokens don't sit in localStorage indefinitely,
  // un-renewed and un-revoked by our own logout flow. Idempotent, safe on
  // every boot. Delete alongside the rest of the @nuxtjs/auth-next removal
  // at Stage B, once $auth no longer exists to call this on.
  $auth.reset();

  let localStore;
  if (!process.server) {
    localStore = localforage.createInstance({
      // List of drivers used
      driver: [localforage.INDEXEDDB, localforage.LOCALSTORAGE],
      // Prefix all storage keys to prevent conflicts
      name: 'BAM-cache'
    });

    await enforceAuthStateVersion(localStore);
  }

  /**
   * Creating and injecting the SDK for subsequent use
   */
  const sdkState = (await localStore?.getItem(sdkKey)) as SdkOptions | null;
  const defaultTimeout = 120000;

  let sdk: SDK;
  if (sdkState != null) {
    sdk = await SDK.build({
      baseUrl: sdkState.baseUrl,
      version: sdkState.version,
      timeout: defaultTimeout
    });
  } else {
    sdk = await SDK.build({
      baseUrl: `https://${$config.apiBaseUrl}`,
      timeout: defaultTimeout
    });
  }

  initAuth(sdk, app.pinia);

  if (sdkState?.credentials != null) {
    const credentials = CredentialFactory.build(
      sdkState.credentials as ICredentialData
    );
    await sdk.authorize(credentials, sdkState.organizer);
  }

  const services = {
    account: account(sdk)
  };

  // Keep the persisted SDK state in sync, independent of the identity
  // mirroring in initAuth() above.
  function persistSdkState() {
    return localStore.setItem(sdkKey, sdk.serialize());
  }

  sdk.eventEmitter.on('authorizationChanged', persistSdkState);
  sdk.eventEmitter.on('logout', () => localStore.removeItem(sdkKey));
  sdk.eventEmitter.on('credentialsExpired', () =>
    localStore.removeItem(sdkKey)
  );
  sdk.eventEmitter.on('organizerChanged', persistSdkState);
  // If we do not update the SDK state on the refresh,
  // a regular user will be calling the token refresh on every call after the first expiry
  sdk.eventEmitter.on('tokenRefreshed', persistSdkState);

  sdk.client.defaults.headers['Cache-Control'] = 'no-cache';
  sdk.tenantClient.defaults.headers['Cache-Control'] = 'no-cache';

  provide('sdk', sdk);
  provide('isProduction', $config.env === 'production');
  provide('showLogin', $config.features.showLogin);
  provide('showCrypto', $config.features.showCrypto);
  provide('showTickets', $config.features.showTickets);
  provide('showSecondary', $config.features.showSecondary);
  provide('mainOrganizer', $config.features.mainOrganizer);
  provide('enableOrganizers', $config.features.enableOrganizers);
  provide(
    'requirePhoneVerification',
    $config.features.requirePhoneVerification
  );
  provide('enableMetaTracking', $config.features.enableMetaTracking);
  provide('metaPixelId', $config.features.metaPixelId);
  provide('showReportsDashboard', $config.features.showReportsDashboard);
  provide('showNftCollectibles', $config.features.showNftCollectibles);
  provide('featuredEvents', $config.features.featuredEvents);
  provide('pdfTemplates', $config.features.pdfTemplates);
  provide(
    'showOrganizerReportsDashboard',
    $config.features.showOrganizerReportsDashboard
  );
  provide('services', services);
  provide('maximalRequestBodyLength', $config.maximalRequestBodyLength);
  provide('protectGroupVendorCode', $config.protectGroupVendorCode ?? '');
  provide('protectGroupHostUrl', $config.protectGroupHostUrl);
  provide('protectGroupEnvironment', $config.protectGroupEnvironment);

  // Inject whitelabel branding into the app, if configured
  const whitelabelOrganizerId = $config.whitelabel.organizer;
  if (whitelabelOrganizerId && !app.$whitelabelLite) {
    try {
      const branding = await sdk.account.getBrandingSettings({
        id: whitelabelOrganizerId
      });
      provide('whitelabelLite', {
        ...branding,
        organizerId: whitelabelOrganizerId
      });
    } catch (e) {
      // Ignore this?
    }
  }
});
